import { ApiError } from './types';
import type { ApiErrorItem, PaginatedResult, Pagination } from './types';
import { API_PREFIX } from 'src/shared/constants';
import useAuthStore, { getAccessToken } from 'src/shared/store/authStore';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

// Envelope produced by the Grazia API: `{ data, meta }` on success and
// `{ errors, meta }` on failure. Nothing else in the app depends on this shape
// besides the functions in this file.
interface ApiEnvelope {
  data?: unknown;
  errors?: ApiErrorItem[];
  meta?: {
    requestId?: string;
    pagination?: Pagination;
    totals?: Record<string, number>;
  };
}

const buildError = (status: number, body: ApiEnvelope): ApiError => {
  const message = body.errors?.[0]?.message ?? `HTTP ${status}`;
  return new ApiError(status, message, body.errors ?? [], body.meta?.requestId ?? '');
};

const buildHeaders = (json = true): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const rawSend = async (path: string, options: RequestOptions): Promise<Response> =>
  fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...buildHeaders(!(options.body instanceof FormData)), ...options.headers },
  });

// Single-flight refresh. Several requests can fail with 401 at the same moment
// (a dashboard firing five queries on mount); without this, each would spend
// the refresh token, and the server's rotation-with-reuse-detection would read
// the second one as a stolen token and revoke the whole session. Every caller
// awaits the same in-flight promise instead.
let refreshInFlight: Promise<string | null> | null = null;

const refreshSession = async (): Promise<string | null> => {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      useAuthStore.getState().clearSession();
      return null;
    }
    const body = (await res.json()) as ApiEnvelope;
    const data = body.data as {
      accessToken: string;
      refreshToken: string;
      user: ReturnType<typeof useAuthStore.getState>['user'];
    };
    if (!data?.accessToken || !data.user) {
      useAuthStore.getState().clearSession();
      return null;
    }
    useAuthStore.getState().setSession({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
    });
    return data.accessToken;
  } catch {
    // Network failure during refresh: drop the session rather than leave the
    // app in a half-authenticated state the guard can't reason about.
    useAuthStore.getState().clearSession();
    return null;
  }
};

// The endpoints that own their own 401s: retrying `/auth/login` after a
// refresh would be nonsense, retrying `/auth/refresh` would recurse, and
// `/auth/logout` is best effort. The rest of `/auth/` (`/auth/me`,
// `/auth/change-password`) is ordinary signed-in traffic, whose 401 means an
// expired access token like anywhere else.
const OWN_401_PATHS = ['login', 'refresh', 'logout'].map((p) => `${API_PREFIX}/auth/${p}`);

const send = async (path: string, options: RequestOptions): Promise<Response> => {
  const res = await rawSend(path, options);

  if (res.status !== 401 || OWN_401_PATHS.includes(path)) return res;

  refreshInFlight =
    refreshInFlight ??
    refreshSession().finally(() => {
      refreshInFlight = null;
    });
  const token = await refreshInFlight;
  if (!token) return res;

  return rawSend(path, options);
};

const requestBlob = async (path: string): Promise<Blob> => {
  const res = await send(path, {});
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiEnvelope;
    throw buildError(res.status, body);
  }
  return res.blob();
};

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const res = await send(path, options);
  if (res.status === 204) return null as T;
  const body = (await res.json()) as ApiEnvelope;
  if (!res.ok) throw buildError(res.status, body);
  return body.data as T;
};

const requestUpload = async <T>(path: string, form: FormData): Promise<T> => {
  const res = await send(path, { method: 'POST', body: form });
  if (res.status === 204) return null as T;
  const body = (await res.json()) as ApiEnvelope;
  if (!res.ok) throw buildError(res.status, body);
  return body.data as T;
};

const requestList = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<PaginatedResult<T>> => {
  const res = await send(path, options);
  const body = (await res.json()) as ApiEnvelope;
  if (!res.ok) throw buildError(res.status, body);
  return {
    items: (body.data as T[]) ?? [],
    pagination: body.meta?.pagination as Pagination,
    totals: body.meta?.totals,
  };
};

/**
 * Turns a path the API returned into something the browser can load directly.
 *
 * Signed media links come back as paths (`/api/v1/media/...`) rather than
 * absolute URLs, because the API behind a proxy cannot reliably know its own
 * public hostname. An `<img src>` on a path would resolve against the *app's*
 * origin, which in development is Vite on another port. This is the one place
 * that knows the base URL, so it is the one place that joins them.
 */
export const apiUrl = (path: string): string => `${BASE_URL}${path}`;

/**
 * A request to an endpoint under `/public`, which takes no session.
 *
 * Never sends `Authorization` and never refreshes. The client's review link is
 * opened on her phone, where there is no session to send — but also, now and
 * then, on the owner's own phone, where there is one: sent along, it would
 * make the API record the client's confirmation as the owner's.
 */
const requestPublic = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json' },
  });
  const body = (await res.json()) as ApiEnvelope;
  if (!res.ok) throw buildError(res.status, body);
  return body.data as T;
};

export const publicClient = {
  get: <T>(path: string) => requestPublic<T>(path),
  post: <T>(path: string, body?: unknown) =>
    requestPublic<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
};

export const httpClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  list: <T>(path: string) => requestList<T>(path),
  download: (path: string) => requestBlob(path),
  upload: <T>(path: string, form: FormData) => requestUpload<T>(path, form),
};
